import { useMemo } from 'react';
import { Box } from '@chakra-ui/react';
import { EmptyField } from 'components/elements/EmptyField';
import { ExternalLink } from 'components/elements/ExternalLink';
import { Text } from 'components/elements/Text';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { ModalHeader } from 'components/layout/ModalHeader';
import { ModalStatus } from 'components/layout/ModalStatus';
import MasterDocumentModal from 'components/modals/documentModals/MasterDocumentModal';
import DetailBody from 'components/widgets/DocumentDetailModal/DetailBody';
import DocumentRelatedCompanies from 'components/widgets/DocumentModals/DocumentRelatedCompanies';
import { InfoModal } from 'components/widgets/InfoModal';
import { usePlatV4 } from 'hooks/useDocuments';
import { useGetModalActions, useGetQueryParam } from 'hooks/useModal';
import { usePlat } from 'hooks/usePlat';
import { PDFMaterial } from 'types/Documents';
import { trackProjectMaterials } from 'utils/analytics';
import { parseRelatedContactsIntoList } from 'utils/contacts';

const capitalize = {
  textTransform: 'capitalize',
};

const ellipsisContainer = {
  maxWidth: '400px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  display: 'block',
};

const pdfMaterialContainer = {
  maxWidth: '100%',
  gap: 2,
};

const wordBreak = { wordBreak: 'break-all' };

const PlatModal = ({
  enableFetchByDocumentId = false,
}: {
  enableFetchByDocumentId?: boolean;
}) => {
  let resolvedDocumentId: string | undefined;
  let result: any;

  if (enableFetchByDocumentId) {
    const documentId = useGetQueryParam('documentId');
    resolvedDocumentId = documentId || undefined;
    result = usePlatV4(documentId || undefined);
  } else {
    const documentUuid = useGetQueryParam('plat');
    resolvedDocumentId = documentUuid || undefined;
    result = usePlat(documentUuid);
  }

  const { Skeleton, ...modalProps } = useGetModalActions(result.isLoading);

  const permitUrl = result.data?.document_url;

  const {
    land_use,
    developer,
    permittype,
    applicant,
    applicant_contact_name,
    applicant_phone,
    subdivision_name,
  } = result.data?.metadata || {};

  const relatedContacts = useMemo(
    () => parseRelatedContactsIntoList(result.data?.metadata?.relatedcontacts || []),
    [result.data?.metadata],
  );

  const { data } = result;
  const hasMaterials =
    data?.metadata?.pdf_material_urls && data?.metadata?.pdf_material_urls.length > 0;

  const relatedContactsWithApplicant = useMemo(() => {
    const applicantContact = {
      type: 'Applicant',
      phone: applicant_phone?.toString() || null,
      name: applicant_contact_name || null,
      company: null,
      email: null,
      mailing_addresses: null,
      physical_addresses: null,
      license_number: null,
      same_as: null,
      isperson: null,
      isorganization: null,
    };

    let developerContact = null;

    if (applicant_contact_name === developer) {
      applicantContact.type = 'Applicant/Developer';
    } else {
      developerContact = {
        type: 'Developer',
        company: developer || null,
        name: null,
        phone: null,
        email: null,
        mailing_addresses: null,
        physical_addresses: null,
        license_number: null,
        same_as: null,
        isperson: null,
        isorganization: null,
      };
    }

    const contacts = [...relatedContacts, applicantContact];

    if (developerContact) {
      contacts.push(developerContact);
    }

    return contacts;
  }, [relatedContacts, applicant, developer]);

  return (
    <InfoModal
      {...modalProps}
      header={
        <ModalHeader Skeleton={Skeleton} type="Plat">
          {result.data?.metadata?.permitnum}
        </ModalHeader>
      }
    >
      <DetailBody>
        <MasterDocumentModal
          address={result.data?.metadata?.parcel_address ?? ''}
          city={result.data?.city ?? ''}
          state_code={result.data?.state_code ?? ''}
          isLoading={result.isLoading}
        />
        <DocumentRelatedCompanies
          contacts={relatedContactsWithApplicant}
          companies={result.data?.companies}
          documentId={resolvedDocumentId}
          prefetchedAiContactSuggestionsByRelatedContactId={
            result.data?.ai_related_contact_suggestions_by_contact_id
          }
          Skeleton={Skeleton}
        />

        <DetailBody.Row label="Subdivision Name">
          <Skeleton variant="text" noOfLines={1}>
            {subdivision_name ? (
              <Text sx={capitalize}>{subdivision_name}</Text>
            ) : (
              <EmptyField />
            )}
          </Skeleton>
        </DetailBody.Row>
        <DetailBody.Row label="Category" hideDivider>
          <Skeleton variant="text" noOfLines={1}>
            {permittype ? <Text sx={capitalize}>{permittype}</Text> : <EmptyField />}
          </Skeleton>
        </DetailBody.Row>
        <DetailBody.Row label="Land Use" hideDivider>
          <Skeleton variant="text" noOfLines={1}>
            {land_use ? <Text sx={capitalize}>{land_use}</Text> : <EmptyField />}
          </Skeleton>
        </DetailBody.Row>
        <ModalStatus activities={result.data?.activities} Skeleton={Skeleton} />

        <DetailBody.Row label="Application">
          <Skeleton variant="text" noOfLines={1}>
            {permitUrl ? (
              <ExternalLink
                to={permitUrl}
                onClick={() => trackProjectMaterials(permitUrl || '')}
              >
                View application
              </ExternalLink>
            ) : (
              <EmptyField />
            )}
          </Skeleton>
        </DetailBody.Row>
        {/* Relevant Materials Section */}
        {(result.isLoading || hasMaterials) && (
          <DetailBody.Row label="Relevant Materials">
            <Skeleton variant="text" noOfLines={result.isLoading ? 2 : 0}>
              {hasMaterials && (
                <FlexColumn sx={pdfMaterialContainer}>
                  {data?.metadata.pdf_material_urls.map((material: PDFMaterial) => (
                    <ExternalLink
                      key={`pdf-material-${material.document_url}`}
                      to={material.document_url}
                      onClick={() => trackProjectMaterials(material.document_url)}
                      sx={wordBreak}
                    >
                      <Box sx={ellipsisContainer}>{material.document_url}</Box>
                    </ExternalLink>
                  ))}
                </FlexColumn>
              )}
            </Skeleton>
          </DetailBody.Row>
        )}
      </DetailBody>
    </InfoModal>
  );
};

export default PlatModal;
